import React, { useState } from 'react';
import { Users, Plus, Copy, Check, Settings, KeyRound, Trash2, X } from 'lucide-react';
import { useDashboard } from '../../DashboardContext';
import { S } from '../../lib/constants';

const genPassword = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
  return Array.from(crypto.getRandomValues(new Uint32Array(16)), n => chars[n % chars.length]).join('');
};

export default function ClientsSection() {
  const { planMgr } = useDashboard();
  const [form, setForm] = useState({ displayName: '', username: '', password: genPassword() });
  const [error, setError] = useState('');
  const [created, setCreated] = useState(null); // {username, password} shown once after creation
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);

  // Managing an existing client
  const [editing, setEditing] = useState(null);   // client object being managed
  const [edit, setEdit] = useState({ displayName: '', username: '' });
  const [editError, setEditError] = useState('');
  const [editBusy, setEditBusy] = useState(false);
  const [newPassword, setNewPassword] = useState(null); // shown once after a reset
  const [confirmDelete, setConfirmDelete] = useState('');

  if (!planMgr?.online) {
    return (
      <div style={{ ...S.card }} className="p-8 text-center">
        <Users className="w-8 h-8 mx-auto mb-3" style={{ color: '#D4D4D4' }} />
        <h3 className="font-semibold mb-1" style={{ color: '#1A1A1A', fontSize: 16 }}>Client workspaces are offline</h3>
        <p className="text-sm mx-auto" style={{ color: '#737373', maxWidth: 420 }}>
          You're exploring in offline mode. Projections work, but client accounts and saved
          plans sync through your MinerOps account. Sign in on a full deployment to continue.
        </p>
      </div>
    );
  }
  const { clients, activeClientId, setActiveClientId, addClient, isClient,
          editClient, resetClientPassword, removeClient } = planMgr;
  if (isClient) return null;

  const openManage = (c) => {
    setEditing(c);
    setEdit({ displayName: c.display_name || '', username: c.username });
    setEditError(''); setNewPassword(null); setConfirmDelete('');
  };
  const closeManage = () => {
    setEditing(null); setEditError(''); setNewPassword(null); setConfirmDelete('');
  };

  const saveEdits = async () => {
    setEditError(''); setEditBusy(true);
    try {
      const updated = await editClient(editing.id, {
        displayName: edit.displayName.trim(),
        username: edit.username.trim().toLowerCase(),
      });
      setEditing(e => ({ ...e, ...updated }));
      setEditError('');
    } catch (e) {
      setEditError(e.message || 'Could not save changes');
    } finally { setEditBusy(false); }
  };

  const doResetPassword = async () => {
    setEditError(''); setEditBusy(true);
    const pw = genPassword();
    try {
      await resetClientPassword(editing.id, pw);
      setNewPassword(pw);
    } catch (e) {
      setEditError(e.message || 'Could not reset password');
    } finally { setEditBusy(false); }
  };

  const doDelete = async () => {
    setEditError(''); setEditBusy(true);
    try {
      await removeClient(editing.id);
      closeManage();
    } catch (e) {
      setEditError(e.message || 'Could not remove client');
      setEditBusy(false);
    }
  };

  const copyText = (text) => {
    navigator.clipboard?.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const inputStyle = { width: '100%', background: '#F5F5F5', border: '1px solid #D4D4D4', borderRadius: 4, padding: '8px 12px', fontSize: 14, color: '#1A1A1A', fontFamily: "'IBM Plex Mono', monospace" };
  const labelStyle = { fontSize: 11, color: '#525252', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' };

  const submit = async () => {
    setError('');
    const username = form.username.trim().toLowerCase();
    if (username.length < 2) { setError('Username must be at least 2 characters'); return; }
    if (form.password.length < 12) { setError('Password must be at least 12 characters'); return; }
    setBusy(true);
    try {
      await addClient(username, form.password, form.displayName.trim() || null);
      setCreated({ username, password: form.password });
      setForm({ displayName: '', username: '', password: genPassword() });
    } catch (e) {
      setError(e.message || 'Failed to create client');
    } finally { setBusy(false); }
  };

  const copyCreds = () => {
    navigator.clipboard?.writeText(`MinerOps login\nUsername: ${created.username}\nPassword: ${created.password}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-6">
      <div style={{ ...S.card }} className="p-5">
        <h3 className="font-semibold flex items-center gap-2 mb-1" style={{ color: '#1A1A1A', fontSize: 16 }}>
          <Users className="w-4 h-4" style={{ color: S.C.gold }} /> Clients
        </h3>
        <p className="text-xs mb-4" style={{ color: '#737373' }}>
          Each client gets their own login. Plans you build appear in their dashboard; they can view them,
          fork their own versions, and compare against your plan, but they can never edit your original.
        </p>

        {/* Client list */}
        {clients.length ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
            {clients.map(c => (
              <div key={c.id}
                style={{
                  background: c.id === activeClientId ? '#FEF9EC' : '#FFFFFF',
                  border: c.id === activeClientId ? '2px solid #92700C' : '1px solid #D4D4D4',
                  borderRadius: 8, padding: 16,
                }}>
                <button onClick={() => setActiveClientId(c.id)} className="text-left w-full"
                  style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>
                  <div className="font-semibold text-sm" style={{ color: '#1A1A1A' }}>{c.display_name || c.username}</div>
                  <div className="text-xs mt-0.5" style={{ color: '#737373', fontFamily: "'IBM Plex Mono', monospace" }}>@{c.username}</div>
                  <div className="text-xs mt-2" style={{ color: '#525252' }}>{c.plan_count} plan{c.plan_count === 1 ? '' : 's'}</div>
                  {c.id === activeClientId && <div className="text-xs mt-1 font-medium" style={{ color: '#92700C' }}>Active. Plans load in the Plan bar above</div>}
                </button>
                <button onClick={() => openManage(c)}
                  className="flex items-center gap-1.5 mt-3 px-2.5 py-1.5 text-xs"
                  style={{ background: 'transparent', border: '1px solid #D4D4D4', borderRadius: 4, color: '#525252', cursor: 'pointer' }}>
                  <Settings className="w-3.5 h-3.5" /> Manage
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-sm italic mb-6" style={{ color: '#737373' }}>No clients yet. Create the first one below.</div>
        )}

        {/* One-time credentials banner */}
        {created && (
          <div className="p-4 mb-6 flex items-center gap-4 flex-wrap" style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 8 }}>
            <div>
              <div className="text-sm font-semibold" style={{ color: '#15803D' }}>Client created. Share these credentials now</div>
              <div className="text-xs mt-1" style={{ color: '#333', fontFamily: "'IBM Plex Mono', monospace" }}>
                {created.username} / {created.password}
              </div>
              <div className="text-xs mt-1" style={{ color: '#737373' }}>The password is not stored in plain text and cannot be shown again.</div>
            </div>
            <button onClick={copyCreds} className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium"
              style={{ background: '#15803D', color: '#FFF', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
              {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />} {copied ? 'Copied' : 'Copy credentials'}
            </button>
          </div>
        )}

        {/* Add client */}
        <div className="p-4" style={{ ...S.innerCard, borderRadius: 8 }}>
          <h4 className="text-sm font-semibold mb-3" style={{ color: '#1A1A1A' }}>Add a client</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
            <div>
              <label style={labelStyle}>Client name</label>
              <input value={form.displayName} onChange={e => setForm(f => ({ ...f, displayName: e.target.value }))} placeholder="Jane Doe" style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Login username</label>
              <input value={form.username} onChange={e => setForm(f => ({ ...f, username: e.target.value }))} placeholder="janedoe" style={inputStyle} autoCapitalize="none" />
            </div>
            <div>
              <label style={labelStyle}>Temporary password</label>
              <div className="flex gap-2">
                <input value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} style={inputStyle} />
                <button onClick={() => setForm(f => ({ ...f, password: genPassword() }))} title="Generate"
                  style={{ padding: '0 12px', background: '#F5F5F5', border: '1px solid #D4D4D4', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}>↻</button>
              </div>
            </div>
          </div>
          {error && <div className="text-xs mb-3 px-3 py-2" style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 4, color: '#DC2626' }}>{error}</div>}
          <button onClick={submit} disabled={busy}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium"
            style={{ background: busy ? '#D4D4D4' : '#1A1A1A', color: '#FFF', border: 'none', borderRadius: 4, cursor: busy ? 'wait' : 'pointer' }}>
            <Plus className="w-4 h-4" /> Create client login
          </button>
        </div>
      </div>

      {/* ── Manage a client ── */}
      {editing && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 100, background: 'rgba(26,26,26,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
        }}>
          <div style={{
            width: '100%', maxWidth: 520, maxHeight: '90vh', overflowY: 'auto',
            background: '#FFFFFF', border: '1px solid #D4D4D4', borderLeft: '6px solid #92700C',
            borderRadius: 8, padding: '26px 30px',
          }}>
            <div className="flex items-start justify-between gap-4 mb-1">
              <h3 className="font-semibold" style={{ color: '#1A1A1A', fontSize: 18 }}>
                {editing.display_name || editing.username}
              </h3>
              <button onClick={closeManage} aria-label="Close"
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#737373', padding: 4 }}>
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-xs mb-5" style={{ color: '#737373' }}>
              {editing.plan_count} plan{editing.plan_count === 1 ? '' : 's'} in this dashboard.
            </p>

            {/* Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
              <div>
                <label style={labelStyle}>Client name</label>
                <input value={edit.displayName} onChange={e => setEdit(v => ({ ...v, displayName: e.target.value }))} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Login username</label>
                <input value={edit.username} onChange={e => setEdit(v => ({ ...v, username: e.target.value }))} style={inputStyle} autoCapitalize="none" />
              </div>
            </div>
            <p className="text-xs mb-4" style={{ color: '#737373' }}>
              Changing the username changes what they sign in with. Tell them before you save it.
            </p>
            <button onClick={saveEdits} disabled={editBusy}
              className="px-4 py-2 text-sm font-medium mb-6"
              style={{ background: editBusy ? '#D4D4D4' : '#1A1A1A', color: '#FFF', border: 'none', borderRadius: 4, cursor: editBusy ? 'wait' : 'pointer' }}>
              Save changes
            </button>

            {/* Password */}
            <div className="pt-5 mb-6" style={{ borderTop: '1px solid #E5E5E5' }}>
              <h4 className="text-sm font-semibold mb-1" style={{ color: '#1A1A1A' }}>Password</h4>
              <p className="text-xs mb-3" style={{ color: '#737373' }}>
                Generates a new password and replaces the old one immediately. It is shown once here,
                so copy it before closing. There is no email delivery, so you pass it on yourself.
              </p>
              {newPassword ? (
                <div className="p-3 flex items-center gap-3 flex-wrap" style={{ background: '#F5F5F5', borderLeft: '6px solid #15803D', borderRadius: '0 4px 4px 0' }}>
                  <div>
                    <div className="text-xs" style={{ color: '#525252' }}>New password for @{editing.username}</div>
                    <div className="text-sm font-semibold" style={{ color: '#1A1A1A', fontFamily: "'IBM Plex Mono', monospace" }}>{newPassword}</div>
                  </div>
                  <button onClick={() => copyText(`MinerOps login\nUsername: ${editing.username}\nPassword: ${newPassword}`)}
                    className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium"
                    style={{ background: '#15803D', color: '#FFF', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
                    {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />} {copied ? 'Copied' : 'Copy'}
                  </button>
                </div>
              ) : (
                <button onClick={doResetPassword} disabled={editBusy}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium"
                  style={{ background: 'transparent', border: '1px solid #D4D4D4', color: '#1A1A1A', borderRadius: 4, cursor: editBusy ? 'wait' : 'pointer' }}>
                  <KeyRound className="w-4 h-4" /> Reset password
                </button>
              )}
            </div>

            {/* Remove */}
            <div className="pt-5" style={{ borderTop: '1px solid #E5E5E5' }}>
              <h4 className="text-sm font-semibold mb-1" style={{ color: '#B91C1C' }}>Remove client</h4>
              <p className="text-xs mb-3" style={{ color: '#737373' }}>
                Deletes their login and all {editing.plan_count} plan{editing.plan_count === 1 ? '' : 's'} in their
                dashboard, including any options they built. This cannot be undone.
                Type <strong style={{ color: '#525252', fontFamily: "'IBM Plex Mono', monospace" }}>{editing.username}</strong> to confirm.
              </p>
              <div className="flex gap-2 flex-wrap">
                <input value={confirmDelete} onChange={e => setConfirmDelete(e.target.value)}
                  placeholder={editing.username} style={{ ...inputStyle, maxWidth: 220 }} autoCapitalize="none" />
                <button onClick={doDelete} disabled={editBusy || confirmDelete.trim() !== editing.username}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium"
                  style={{
                    background: confirmDelete.trim() === editing.username ? '#B91C1C' : '#E5E5E5',
                    color: confirmDelete.trim() === editing.username ? '#FFF' : '#999999',
                    border: 'none', borderRadius: 4,
                    cursor: confirmDelete.trim() === editing.username && !editBusy ? 'pointer' : 'default',
                  }}>
                  <Trash2 className="w-4 h-4" /> Remove
                </button>
              </div>
            </div>

            {editError && (
              <div className="text-xs mt-4 px-3 py-2" style={{ background: '#F5F5F5', borderLeft: '6px solid #B91C1C', borderRadius: '0 4px 4px 0', color: '#333' }}>
                {editError}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
